import {
  useState,
  useRef,
  useCallback,
  forwardRef,
  Ref,
  useId,
  MutableRefObject,
  useEffect,
  SyntheticEvent,
  ChangeEvent,
  Fragment,
} from 'react';
import './style.scss';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../i18n/translations';
import {useParams} from 'react-router-dom';
import {
  FormControl,
  Slide,
  Typography,
  Box,
  Autocomplete,
  SvgIcon,
  Theme,
  SxProps,
  AutocompleteRenderInputParams,
  Stack,
  CircularProgress,
} from '@mui/material';
import {LoadingButton} from '@mui/lab';
import {Add, Check, Search} from '@mui/icons-material';
import FlatInput from '../../../flat-input/flat-input';
import {useDebounce, useInfinityScrollItems} from '../../../../hooks';
import {ScrollFetchParams} from '../../../../hooks/infinite-scroll-items';
import {DEBOUNCE_REQUEST} from '../../../../utils/constants';
import {EntityActivitiesRepository} from '../../../../repositories/entity-activities';
import {CarePlanConfigurationItemAutocompleteItem as FlatInputFeatureAutocompleteOption} from '../../../../backend/interfaces/care-plan-configuration-item-autocomplete-item';

export interface FlatInputFeatureAutocompleteProps {
  id?: string;
  label?: string;
  placeholder?: string;
  value?: string;
  errors?: string;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  fetchParams: {
    activityGroupId: string;
    activityTypeId: string;
    carePlanConfigurationId: string;
    activitiesSectorId: string;
  };
  onChange: (selected: string | null) => void;
  sx?: SxProps<Theme>;
}

export const FlatInputFeatureAutocomplete = forwardRef(
  (
    {
      label,
      value,
      placeholder,
      errors,
      required,
      readonly,
      disabled,
      onChange,
      fetchParams,
      sx,
      ...rest
    }: FlatInputFeatureAutocompleteProps,
    ref: Ref<HTMLInputElement>
  ) => {
    const [loadingDefaultValue, setLoadingDefaultValue] = useState(false);

    const [inputValue, setInputValue] = useState<string | undefined>();

    const [selectedOption, setSelectedOption] = useState<FlatInputFeatureAutocompleteOption | null>(
      null
    );

    const [isTyping, setIsTyping] = useState(false);

    let {entityId, id: seniorId}: {entityId?: string; id?: string} = useParams();

    const loaderRef = useRef() as MutableRefObject<HTMLButtonElement>;

    const id = useId();

    const {t} = useTranslation();

    const handleGetConfigurationFeatureOptions = useCallback(
      async ({limit, skip, search}: ScrollFetchParams) => {
        const {activityGroupId, activityTypeId, carePlanConfigurationId, activitiesSectorId} =
          fetchParams;

        // Check for required params
        if (
          !entityId ||
          !activitiesSectorId ||
          !activityGroupId ||
          !activityTypeId ||
          !carePlanConfigurationId
        )
          throw new Error('Missing required params');

        // Load data
        const result =
          await EntityActivitiesRepository.instance.getCareplanConfigurationOptionItems(
            entityId,
            activitiesSectorId,
            activityGroupId,
            activityTypeId,
            carePlanConfigurationId,
            skip,
            limit,
            seniorId!,
            search
          );

        // Return result
        return result;
      },
      [seniorId, entityId, fetchParams]
    );

    const getDefaultSelectedItem = useCallback(async () => {
      setLoadingDefaultValue(true);

      const {activityGroupId, activityTypeId, carePlanConfigurationId, activitiesSectorId} =
        fetchParams;

      // Check for required params
      if (
        !entityId ||
        !activitiesSectorId ||
        !activityGroupId ||
        !activityTypeId ||
        !carePlanConfigurationId ||
        !value
      )
        throw new Error('Missing required params');

      const option =
        await EntityActivitiesRepository.instance.getGroupCarePlanConfigurationDataValue(
          entityId,
          activitiesSectorId,
          activityGroupId,
          activityTypeId,
          carePlanConfigurationId,
          value
        );

      setSelectedOption(option);

      setLoadingDefaultValue(false);
    }, [entityId, fetchParams, value]);

    const {
      counter,
      isLoading,
      hasNextPage,
      handleUpdateCurrentSearch,
      handleGetNextPage,
      data: options,
      error: fetchError,
    } = useInfinityScrollItems<FlatInputFeatureAutocompleteOption>({
      fetchData: handleGetConfigurationFeatureOptions,
      pageSize: 10,
      updateUrl: false,
      loaderRef,
    });

    const handleOnChange = (
      _event: SyntheticEvent<Element, Event>,
      selected: FlatInputFeatureAutocompleteOption | null
    ) => {
      if (selected) {
        setSelectedOption(selected);
        onChange(selected.id);
      } else {
        setSelectedOption(null);
        onChange(null);
      }
    };

    const handleSearch = () => {
      handleUpdateCurrentSearch(inputValue);
      setIsTyping(false);
    };

    const hasError = !!errors;

    const uniqueId = rest.id ?? `flat_autocomplete_input_${id}`;

    useEffect(() => {
      if (!loadingDefaultValue) {
        if (value && typeof value === 'string' && selectedOption === null) getDefaultSelectedItem();
      }
    }, [getDefaultSelectedItem, loadingDefaultValue, selectedOption, value]);

    useDebounce(handleSearch, DEBOUNCE_REQUEST, [handleUpdateCurrentSearch, inputValue]);

    return (
      <FormControl className={`flat-autocomplete-input ${disabled ? 'disabled' : ''}`}>
        {label && (
          <Box className="label-container">
            <Typography component="label" htmlFor={uniqueId} noWrap>
              {label}
            </Typography>
            {required && !disabled && <Typography component="span">*</Typography>}
          </Box>
        )}

        <Autocomplete
          ref={ref}
          // @ts-ignore
          value={selectedOption}
          readOnly={readonly}
          disabled={disabled}
          onChange={handleOnChange}
          options={options}
          loading={isLoading || isTyping}
          getOptionLabel={(option: FlatInputFeatureAutocompleteOption) => option.name}
          isOptionEqualToValue={(option: FlatInputFeatureAutocompleteOption) =>
            option.id === selectedOption?.id
          }
          filterOptions={(displayOptions, state) => {
            return displayOptions.filter(
              (option) =>
                option.name.toLowerCase().trim().includes(state.inputValue.toLowerCase().trim()) ||
                option.id.toLowerCase().trim().includes(state.inputValue.toLowerCase().trim())
            );
          }}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <FlatInput
              {...params}
              value={inputValue}
              placeholder={t(translations.dynamic_form.feature_fields.autocomplete.placeholder)}
              onChange={(event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                setInputValue(event.target.value);
                setIsTyping(true);
              }}
              InputLabelProps={{
                ...params.InputLabelProps,
                children: null,
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: <Search htmlColor="var(--dcm-gray-general)" />,
              }}
              errors={errors}
            />
          )}
          renderOption={(renderProps, option: FlatInputFeatureAutocompleteOption) => {
            const selected = selectedOption?.id === option.id;
            const lastOption = option.id === options[options.length - 1].id;

            return (
              <Fragment key={`${option.name}-${option.id}`}>
                <li
                  {...renderProps}
                  style={{
                    flexDirection: 'column',
                  }}
                >
                  <Stack
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{width: '100%', padding: 0, margin: 0}}
                  >
                    <Typography
                      component="span"
                      noWrap
                      sx={{
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {option.name}
                    </Typography>

                    {selected && (
                      <SvgIcon sx={{fontSize: 'small'}}>
                        <Check />
                      </SvgIcon>
                    )}
                  </Stack>
                </li>
                {lastOption && (
                  <li style={{flexDirection: 'column'}}>
                    <Stack
                      justifyContent="center"
                      alignItems="center"
                      gap="10px"
                      sx={{mt: '10px', padding: '10px'}}
                    >
                      <LoadingButton
                        variant="outlined"
                        color="primary"
                        size="small"
                        endIcon={isLoading ? <CircularProgress size={16} /> : <Add />}
                        loadingPosition={isLoading ? 'start' : undefined}
                        disabled={!hasNextPage || isLoading}
                        onClick={handleGetNextPage}
                      >
                        {t(translations.dynamic_form.feature_fields.autocomplete.load_more)}
                      </LoadingButton>
                      <Typography fontSize="12px">
                        {t(translations.dynamic_form.feature_fields.autocomplete.counter, {
                          current: options.length,
                          max: counter,
                        })}
                      </Typography>
                    </Stack>
                  </li>
                )}
              </Fragment>
            );
          }}
          loadingText={t(translations.dynamic_form.feature_fields.autocomplete.loading)}
          noOptionsText={
            fetchError
              ? fetchError
              : t(translations.dynamic_form.feature_fields.autocomplete.no_options)
          }
          disableClearable
        />

        <Slide direction="left" in={hasError} mountOnEnter unmountOnExit>
          <span className="input-error">{errors}</span>
        </Slide>
      </FormControl>
    );
  }
);
